/**
 * Constants for reader selection tool validation and UI behavior.
 *
 * These limits control validation for various reader features (dictionary,
 * translate, TTS, AI) and ensure consistent UX across the application.
 */

/**
 * Duration in milliseconds for tooltip auto-hide across all validation components.
 */
export const TOOLTIP_AUTO_HIDE_DURATION = 3000;

/**
 * Dictionary feature validation limits.
 */
export const DICTIONARY_MAX_WORDS = 5;

/**
 * Translation feature validation limits.
 *
 * Character limit rationale (1000 chars):
 * - User experience: Covers typical paragraph-level translations
 * - API efficiency: ~250 input tokens, ~500 output tokens (600 budget with margin)
 * - Encrypted payload: Max 1900 characters (accounts for AES + base64 overhead)
 * - Comparison: 5x smaller than AI features, 10x smaller than TTS
 */
export const TRANSLATE_MAX_CHARACTERS = 1000;

/**
 * Text-to-Speech (TTS) feature validation limits.
 *
 * Frontend limit rationale (10,000 chars):
 * - Audio duration: ~10-11 minutes (realistic listening session)
 * - API efficiency: 20 sequential requests (text chunked at ~500 chars)
 * - User experience: Lower failure rate, faster processing
 * - Comparison: 2.5x larger than AI features, 12.5x larger than translate
 */
export const TTS_MAX_CHARACTERS = 10000;

/**
 * Backend chunk size for TTS requests.
 * Frontend chunks text into ~500 character segments before sending to backend.
 * See: _shared_/UI/ReaderSelectionTool/integrations/amazon-polly.vue:211
 */
export const TTS_CHUNK_SIZE = 500;

/**
 * Playback speed options for TTS.
 * Range: 0.5x (slow) to 2x (fast), based on common accessibility standards.
 * - 0.5x-0.75x: For users who need slower speech
 * - 1x: Normal speed (default)
 * - 1.25x-2x: For users who prefer faster playback
 */
export const TTS_PLAYBACK_SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

/**
 * Default playback speed for TTS (normal speed).
 */
export const TTS_DEFAULT_PLAYBACK_SPEED = 1;

/**
 * AI Integration feature validation limits.
 */
export const AI_MIN_CHARACTERS = 10;
export const AI_MAX_CHARACTERS = 4000;
